import { Grid, Box, CardMedia, useMediaQuery } from '@material-ui/core';
import { makeStyles, Theme, createStyles, useTheme } from '@material-ui/core/styles';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useCurrentUser } from '../../../../Session';
import {
    DealerQuotesStatus,
    FullQuotationDataFragment,
    FullPurchaseTransactionDataFragment,
    GetPurchaseTransactionLiveDisplayQueryResult,
    GetPurchaseTransactionQueryResult,
    GetQuotationQueryResult,
    LiveDealerQuotesDataFragment,
} from '../../../../api';
import { LocationHistory } from '../../../../components/LocationHistory';
import TabPanel from '../../../../components/TabPanel';
import { MobileMenuTabs, DesktopMenuTabs } from '../../components';
import HandoverTab from './HandoverTab';
import QuotationTab from './QuotationTab';
import TransactionTab from './TransactionTab';
import ValuationTab from './ValuationTab';
import VehicleInfo from './VehicleInfo';
import VehicleTab from './VehicleTab';

export type ViewPurchaseTransactionBodyProps = {
    displayQuotation: boolean;
    purchaseTransaction: FullPurchaseTransactionDataFragment;
    quotation?: FullQuotationDataFragment;
    liveQuotation?: LiveDealerQuotesDataFragment;
    quotationLoading: boolean;
    refetchQuotation: GetQuotationQueryResult['refetch'];
    refetchLiveQuotation: GetPurchaseTransactionLiveDisplayQueryResult['refetch'];
    refetchPurchaseTransaction: GetPurchaseTransactionQueryResult['refetch'];
    openCancelTransactionDialog: () => void;
    showConfirmDialog: () => void;
    goToVehicleDetails: () => void;
    openCompleteDrawer: () => void;
};

const a11yProps = (index: string) => ({
    id: `scrollable-force-tab-${index}`,
    'aria-controls': `scrollable-force-tabpanel-${index}`,
});

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            [theme.breakpoints.down('sm')]: {
                marginTop: theme.spacing(8),
            },
        },
        body: {
            [theme.breakpoints.down('sm')]: {
                padding: theme.spacing(3),
                paddingRight: theme.spacing(4),
            },
        },
        carImage: {
            height: theme.spacing(30),
            marginBottom: theme.spacing(2),
            borderRadius: theme.spacing(1),
        },
    })
);

const ViewPurchaseTransactionBody = ({
    displayQuotation,
    purchaseTransaction,
    quotation,
    liveQuotation,
    quotationLoading,
    refetchQuotation,
    refetchLiveQuotation,
    refetchPurchaseTransaction,
    openCancelTransactionDialog,
    showConfirmDialog,
    goToVehicleDetails,
    openCompleteDrawer,
}: ViewPurchaseTransactionBodyProps) => {
    const styles = useStyles();
    const [tabName, setTabName] = useState('transaction');
    const location = useLocation();
    const currentUser = useCurrentUser();

    const { refreshPurchaseTransactionDetail } = (location.state || {}) as LocationHistory;

    // set tab to default once id gets changed and when user clicks on push notification
    useEffect(() => setTabName('transaction'), [purchaseTransaction.id, refreshPurchaseTransactionDetail]);

    // Refetch purchase transaction when the valuation tab is active
    useEffect(() => {
        if (tabName === 'valuation') {
            refetchPurchaseTransaction({ transactionId: purchaseTransaction.id });
        }
    }, [currentUser.type, purchaseTransaction.id, refetchPurchaseTransaction, tabName]);

    const submittedQuoteCount =
        (liveQuotation?.dealersInfo ?? quotation?.dealersInfo)?.filter(
            dealer => dealer.status === DealerQuotesStatus.Quoted
        ).length ?? 0;

    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <>
            <Box className={styles.root} mb={isSmall ? 10 : 0}>
                {isSmall && purchaseTransaction.frontPagePhoto && (
                    <CardMedia className={styles.carImage} image={purchaseTransaction.frontPagePhoto.signedUrl} />
                )}
                <Box className={styles.body}>
                    <Grid spacing={2} container>
                        <Grid lg={5} md={5} sm={12} xs={12} item>
                            <VehicleInfo purchaseTransaction={purchaseTransaction} />
                        </Grid>
                        <Grid lg={7} md={7} sm={12} xs={12} item>
                            {!isSmall && (
                                <DesktopMenuTabs
                                    a11yProps={a11yProps}
                                    displayQuotation={displayQuotation}
                                    setValue={setTabName}
                                    submittedQuoteCount={submittedQuoteCount}
                                    value={tabName}
                                />
                            )}
                            <TabPanel index="transaction" value={tabName}>
                                <TransactionTab purchaseTransaction={purchaseTransaction} />
                            </TabPanel>
                            <TabPanel index="vehicle" value={tabName}>
                                <VehicleTab purchaseTransaction={purchaseTransaction} />
                            </TabPanel>
                            <TabPanel index="valuation" value={tabName}>
                                <ValuationTab
                                    goToVehicleDetails={goToVehicleDetails}
                                    openCancelTransactionDialog={openCancelTransactionDialog}
                                    openCompleteDrawer={openCompleteDrawer}
                                    purchaseTransaction={purchaseTransaction}
                                    refetchQuotation={refetchQuotation}
                                    showConfirmDialog={showConfirmDialog}
                                />
                            </TabPanel>
                            {displayQuotation && (
                                <TabPanel index="quotation" value={tabName}>
                                    <QuotationTab
                                        liveQuotation={liveQuotation}
                                        purchaseTransaction={purchaseTransaction}
                                        quotation={quotation}
                                        quotationLoading={quotationLoading && !quotation}
                                        refetchLiveQuotation={refetchLiveQuotation}
                                        refetchQuotation={refetchQuotation}
                                    />
                                </TabPanel>
                            )}
                            <TabPanel index="handover" value={tabName}>
                                <HandoverTab purchaseTransaction={purchaseTransaction} />
                            </TabPanel>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            {isSmall && (
                <MobileMenuTabs
                    a11yProps={a11yProps}
                    displayQuotation={displayQuotation}
                    setValue={setTabName}
                    submittedQuoteCount={submittedQuoteCount}
                    value={tabName}
                />
            )}
        </>
    );
};

export default ViewPurchaseTransactionBody;
